import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tag, Plus } from "lucide-react";
import { Category } from "@/types/database";
import { AVAILABLE_ICONS, getCategoryIcon } from "@/constants/categoryIcons";
import { useAuth } from "@/hooks/useAuth";

interface QuickCategoryModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tipo: "entrada" | "saida" | "ambos";
    onSuccess: (category: Category) => void;
}

const COLORS = [
    { value: "#3B82F6", label: "Azul" },
    { value: "#10B981", label: "Verde" },
    { value: "#F59E0B", label: "Amarelo" },
    { value: "#EF4444", label: "Vermelho" },
    { value: "#8B5CF6", label: "Roxo" },
    { value: "#06B6D4", label: "Ciano" },
    { value: "#F97316", label: "Laranja" },
    { value: "#84CC16", label: "Lima" },
    { value: "#EC4899", label: "Rosa" },
    { value: "#6B7280", label: "Cinza" },
];

export function QuickCategoryModal({ open, onOpenChange, tipo, onSuccess }: QuickCategoryModalProps) {
    const [nome, setNome] = useState("");
    const [cor, setCor] = useState(COLORS[0].value);
    const [icone, setIcone] = useState("default");
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nome.trim() || !user) return;

        setLoading(true);
        try {
            const { data, error } = await supabase
                .from("categorias")
                .insert({
                    nome: nome.trim(),
                    tipo: tipo,
                    cor: cor,
                    icone: icone,
                    user_id: user.id
                })
                .select()
                .single();

            if (error) {
                if (error.code === "23505") {
                    toast.error("Uma categoria com este nome já existe.");
                } else {
                    throw error;
                }
                return;
            }

            toast.success("Categoria criada com sucesso!");
            onSuccess(data as Category);
            handleClose();
        } catch (error) {
            console.error("Erro ao criar categoria:", error);
            toast.error("Erro ao criar categoria");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setNome("");
        setCor(COLORS[0].value);
        setIcone("default");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Plus className="w-5 h-5 text-blue-600" />
                        Nova Categoria
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="quick-nome">Nome da Categoria</Label>
                        <Input
                            id="quick-nome"
                            placeholder="Ex: Lazer, Alimentação..."
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="quick-cor">Cor</Label>
                            <Select value={cor} onValueChange={setCor}>
                                <SelectTrigger id="quick-cor">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {COLORS.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.value }} />
                                                <span className="text-xs">{c.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="quick-icone">Ícone</Label>
                            <Select value={icone} onValueChange={setIcone}>
                                <SelectTrigger id="quick-icone">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                    {AVAILABLE_ICONS.map((item) => (
                                        <SelectItem key={item.value} value={item.value}>
                                            <div className="flex items-center gap-2">
                                                <item.icon className="h-4 w-4" />
                                                <span className="text-xs">{item.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter className="pt-4">
                        <Button type="button" variant="ghost" onClick={handleClose}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading || !nome.trim()}>
                            {loading ? "Criando..." : "Criar Categoria"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
